/*
  Kiwi Ears · homepage hero slider (Rene 2026-09-01) — sections/kh-heroslider.

  ES module: `three` resolves through the importmap in layout/theme.liquid.
  GSAP arrives as a deferred classic script that executes AFTER this module in
  tree order, so boot waits for DOMContentLoaded when window.gsap is not there
  yet — by then every deferred script has run.

  THREE LAYERS, EACH ONE OPTIONAL:
    · The <img> stack is the truth — no JS, no GSAP, no WebGL: slide 1 shows.
    · GSAP adds the autoplay timer, the rail-row progress fill, and the text
      choreography. Transitions fall back to an image crossfade.
    · three.js adds the displacement transition between photographs. It boots
      lazily (idle, ≥1024px, motion-OK, every slide has a photo), renders ON
      DEMAND — one draw per tween tick, zero rAF while idle — and any failure
      (no WebGL, context lost, a texture 404) drops back to the crossfade
      without visual damage: the canvas only unhides after a real first frame.

  Reduced motion: no autoplay, no WebGL, transitions become short opacity
  fades. Autoplay also pauses on hover (schema setting), focus within, hidden
  tab, and offscreen.
*/
import * as THREE from 'three';

const armed = new WeakSet();

/* ------------------------------------------------------------------ boot */
function bootAll() {
  document.querySelectorAll('[data-khs]').forEach((root) => {
    if (!armed.has(root)) { armed.add(root); init(root); }
  });
}
if (window.gsap) bootAll();
else document.addEventListener('DOMContentLoaded', bootAll, { once: true });

/* theme editor re-injects the section's DOM — arm the fresh copy */
document.addEventListener('shopify:section:load', (e) => {
  const root = e.target && e.target.querySelector && e.target.querySelector('[data-khs]');
  if (root && !armed.has(root)) { armed.add(root); init(root); }
});

/* ---------------------------------------------------------------- engine */
function init(root) {
  const gsap = window.gsap;
  const slides = Array.from(root.querySelectorAll('[data-khs-slide]'));
  const medias = Array.from(root.querySelectorAll('[data-khs-media]'));
  const gos = Array.from(root.querySelectorAll('[data-khs-go]'));
  const canvas = root.querySelector('[data-khs-canvas]');
  const cur = root.querySelector('[data-khs-cur]');
  const n = slides.length;
  if (!n) return;

  const interval = Math.max(3, parseFloat(root.getAttribute('data-khs-interval')) || 7);
  const hoverPause = root.getAttribute('data-khs-hoverpause') !== 'false';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let idx = 0;
  let busy = false;
  let gl = null;            /* the WebGL layer, when it earns its place */
  let held = { hover: false, focus: false, hidden: false, offscreen: false };

  /* -------------------------------------------------- rail bookkeeping */
  function rowsFor(i) { return gos.filter((b) => +b.getAttribute('data-khs-go') === i); }
  function fillsFor(i) { return rowsFor(i).map((b) => b.querySelector('.khs-fill')).filter(Boolean); }

  function paintRail() {
    /* The nav wears the ACTIVE slide's shape: product slides get the full
       200px rows, clean/reservation slides get the compact form (rows fade
       out in custom.css, arrows + counter stay) so the slide can use the
       whole width (Rene 2026-09-01). */
    const kind = (slides[idx] && slides[idx].getAttribute('data-khs-kind')) || 'product';
    root.setAttribute('data-khs-navmode', kind === 'product' ? 'full' : 'compact');
    root.setAttribute('data-khs-active-kind', kind);
    gos.forEach((b) => {
      const active = +b.getAttribute('data-khs-go') === idx;
      if (active) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
      const fill = b.querySelector('.khs-fill');
      if (fill && !active) fill.style.transform = 'scaleX(0)';
      const secs = b.querySelector('.khs-secs');
      if (secs) secs.hidden = !active || !timer;
    });
    if (cur) cur.textContent = idx + 1;
  }

  /* ------------------------------------------------------ the timer */
  const proxy = { p: 0 };
  let timer = null;
  if (!reduce && n > 1 && gsap) {
    timer = gsap.to(proxy, {
      p: 1, duration: interval, ease: 'none', paused: true,
      onUpdate() {
        const sx = 'scaleX(' + proxy.p.toFixed(4) + ')';
        fillsFor(idx).forEach((f) => { f.style.transform = sx; });
        const left = Math.ceil(interval * (1 - proxy.p));
        rowsFor(idx).forEach((b) => {
          const secs = b.querySelector('.khs-secs');
          if (secs) secs.textContent = left;
        });
      },
      onComplete() { go(idx + 1, 1); },
    });
  }
  function timerSync() {
    if (!timer) return;
    const heldAny = held.hover || held.focus || held.hidden || held.offscreen;
    if (heldAny || busy) timer.pause();
    else timer.play();
  }
  function timerRestart() {
    if (!timer) return;
    proxy.p = 0;
    timer.restart(true);
    timerSync();
  }

  /* --------------------------------------------------- transitions */
  function go(target, dir) {
    if (busy || n < 2) return;
    const next = ((target % n) + n) % n;
    if (next === idx) return;
    busy = true;
    if (timer) timer.pause();

    const from = idx, to = next;
    const outEls = slides[from].querySelectorAll('.khs-item');
    const inEls = slides[to].querySelectorAll('.khs-item');

    slides[to].classList.remove('khs-off');
    slides[to].removeAttribute('inert');

    const done = () => {
      slides[from].classList.add('khs-off');
      slides[from].setAttribute('inert', '');
      idx = to;
      busy = false;
      paintRail();
      timerRestart();
    };

    /* photographs */
    const usedGL = gl && gl.go(from, to, dir);
    if (!usedGL) {
      if (gl) gl.swap(to);
      if (gsap && !reduce) {
        gsap.to(medias[from], { autoAlpha: 0, duration: 0.8, ease: 'power2.inOut' });
        gsap.fromTo(medias[to], { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8, ease: 'power2.inOut' });
      } else {
        medias[from].classList.add('khs-off');
        medias[to].classList.remove('khs-off');
      }
    } else {
      /* the canvas owns the pixels; keep the img stack parked on the target
         so a later GL failure lands on the right photograph */
      medias[from].classList.add('khs-off');
      medias[to].classList.remove('khs-off');
    }

    /* copy */
    if (gsap && !reduce) {
      const tl = gsap.timeline({ onComplete: done });
      tl.to(outEls, { autoAlpha: 0, y: -16, duration: 0.3, stagger: 0.03, ease: 'power2.in' }, 0);
      tl.fromTo(inEls, { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.07, ease: 'power3.out', clearProps: 'visibility' }, 0.22);
    } else if (gsap) {
      const tl = gsap.timeline({ onComplete: done });
      tl.to(outEls, { autoAlpha: 0, duration: 0.15 }, 0);
      tl.fromTo(inEls, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2, clearProps: 'visibility' }, 0.12);
    } else {
      done();
    }
  }

  /* ------------------------------------------------------- controls */
  root.querySelectorAll('[data-khs-prev]').forEach((b) => b.addEventListener('click', () => go(idx - 1, -1)));
  root.querySelectorAll('[data-khs-next]').forEach((b) => b.addEventListener('click', () => go(idx + 1, 1)));
  gos.forEach((b) => b.addEventListener('click', () => {
    const i = +b.getAttribute('data-khs-go');
    go(i, i > idx ? 1 : -1);
  }));
  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1, 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1, -1); }
  });

  /* swipe — the natural phone gesture. Horizontal-dominant moves only, so
     vertical page scrolling over the hero never mis-fires a slide change. */
  let swipe = null;
  root.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    swipe = { x: e.clientX, y: e.clientY };
  }, { passive: true });
  root.addEventListener('pointerup', (e) => {
    if (!swipe) return;
    const dx = e.clientX - swipe.x, dy = e.clientY - swipe.y;
    swipe = null;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 2) {
      go(idx + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
    }
  }, { passive: true });
  root.addEventListener('pointercancel', () => { swipe = null; }, { passive: true });

  if (hoverPause) {
    root.addEventListener('pointerenter', () => { held.hover = true; timerSync(); });
    root.addEventListener('pointerleave', () => { held.hover = false; timerSync(); });
  }
  root.addEventListener('focusin', (e) => {
    /* Hold only for KEYBOARD focus. A mouse click on the next/prev arrows
       focuses them too, and that focus never leaves — which silently parked
       the autoplay forever after one manual click (Rene 2026-09-01).
       :focus-visible is precisely the keyboard/mouse discriminator; where a
       very old browser lacks it, fall back to the safe old behaviour. */
    let kb = true;
    try { kb = !!(e.target && e.target.matches(':focus-visible')); } catch (err) {}
    held.focus = kb;
    timerSync();
  });
  root.addEventListener('focusout', (e) => {
    if (!root.contains(e.relatedTarget)) { held.focus = false; timerSync(); }
  });
  document.addEventListener('visibilitychange', () => {
    held.hidden = document.visibilityState === 'hidden';
    timerSync();
  });
  if (window.IntersectionObserver) {
    new IntersectionObserver((es) => {
      held.offscreen = !es.some((x) => x.isIntersecting);
      timerSync();
    }, { threshold: 0.15 }).observe(root);
  }

  paintRail();
  timerSync();
  if (timer) timer.play();

  /* =============================================== WebGL displacement
     Earned, not assumed: desktop width, motion OK, canvas present, more
     than one slide, and at least one slide with a photograph. Photo-less
     slides (clean/reservation riding the purple glow) coexist with the
     canvas now: the wave runs photo-to-photo, and any leg that touches a
     glow slide crossfades the media stack while gl.swap() fades the canvas
     out of the way (and back in over the next photo, restarting the Ken
     Burns push). Before 2026-09-01 one photo-less slide silently disabled
     the whole GL layer — wave AND Ken Burns. */
  if (reduce || n < 2 || !canvas) return;
  if (!window.matchMedia('(min-width: 1024px)').matches) return;

  const imgs = medias.map((m) => m.querySelector('img[data-khs-tex]'));
  if (!imgs.some(Boolean)) return;

  const start = () => { glBoot(); };
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2500 });
  else setTimeout(start, 900);

  function glBoot() {
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
    } catch (e) { return; }

    const scene = new THREE.Scene();
    const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const uniforms = {
      uT1: { value: null }, uT2: { value: null },
      uS1: { value: new THREE.Vector2(1, 1) }, uS2: { value: new THREE.Vector2(1, 1) },
      uRes: { value: new THREE.Vector2(1, 1) },
      uP: { value: 0 }, uDir: { value: 1 }, uSeed: { value: 0 },
      uKb1: { value: 1 }, uKb2: { value: 1 },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms,
      depthTest: false,
      depthWrite: false,
      vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        precision highp float;
        uniform sampler2D uT1; uniform sampler2D uT2;
        uniform vec2 uS1; uniform vec2 uS2; uniform vec2 uRes;
        uniform float uP; uniform float uDir; uniform float uSeed;
        uniform float uKb1; uniform float uKb2;
        varying vec2 vUv;

        /* CSS object-cover for a texture on the full-bleed plane */
        vec2 cover(vec2 uv, vec2 res, vec2 tex) {
          vec2 s = res / tex;
          float k = max(s.x, s.y);
          vec2 fit = tex * k;
          vec2 off = (res - fit) * 0.5;
          return (uv * res - off) / fit;
        }

        float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
        float noise(vec2 p) {
          vec2 i = floor(p), f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          float a = hash(i), b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }
        float fbm(vec2 p) {
          float v = 0.0, a = 0.5;
          for (int i = 0; i < 3; i++) { v += a * noise(p); p = p * 2.13 + 17.7; a *= 0.5; }
          return v;
        }

        /*
          A PRESSURE WAVE, not a wipe. One turbulent front sweeps in the travel
          direction; everything interesting happens in the band around it:

            - the front line undulates like a waveform (two sines + 3-octave
              fbm), and uSeed re-phases it per transition so no two are alike
            - the OUTGOING image's own luminance steers how hard each pixel is
              torn -- every photograph breaks along its own features
            - a perpendicular swirl near the crest refracts both images,
              liquid-glass rather than sliding paper
            - the old frame presses toward the viewer as it leaves while the
              new one settles down from 8% -- depth instead of a flat swap
            - RGB disperses along the displacement vector inside the band, and
              the crest carries a glint with a breath of the brand green

          The wobble and turbulence are gated by sin(PI*t) and the sweep is
          overdriven (t*1.7 - 0.35) past the widest seam, so at t=0 and t=1
          the frame is EXACTLY the plain covered texture -- the idle frame and
          the underlying <img> stack stay pixel-identical to before.
        */
        void main() {
          float t = smoothstep(0.0, 1.0, clamp(uP, 0.0, 1.0));
          float gate = sin(3.14159 * t);
          float seed = uSeed * 37.0;

          /* axis along travel: 0 where the wave enters, 1 where it exits */
          float x = mix(1.0 - vUv.x, vUv.x, step(0.0, uDir));

          float turb = fbm(vUv * vec2(3.0, 6.0) + seed);
          float wobble = sin(vUv.y * 9.0 + seed) * 0.6 + sin(vUv.y * 23.0 + seed * 1.7) * 0.4;

          float front = t * 1.7 - 0.35;
          float d = x - front + (wobble * 0.05 + (turb - 0.5) * 0.24) * gate;

          float w = 0.16 + 0.10 * turb;                    /* seam width */
          float pe = 1.0 - smoothstep(-w, w, d);           /* 0 = old, 1 = new */
          float crest = 1.0 - smoothstep(0.0, w * 1.6, abs(d));

          /* the outgoing image steers its own tearing */
          float lum = dot(texture2D(uT1, cover(vUv, uRes, uS1)).rgb, vec3(0.299, 0.587, 0.114));
          vec2 disp = vec2(uDir, 0.0) * (0.10 + 0.22 * lum)
                    + vec2(0.0, 1.0) * (turb - 0.5) * 0.16;
          float grip = 0.6 + 0.4 * crest;

          /* Ken Burns: dividing the centred uv by uKb samples a smaller
             central window, i.e. a slow push-in. uKb1 rides the idle tween,
             so at transition start the outgoing frame is already at its
             current zoom and the seam stays continuous; the incoming frame
             enters at uKb2 = 1 and its own push starts after the swap. */
          vec2 c = vec2(0.5);
          vec2 uvOld = (vUv - c) / uKb1 * (1.0 + 0.06 * t) + c + disp * pe * grip;
          vec2 uvNew = (vUv - c) / uKb2 * (1.0 + 0.08 * (1.0 - t)) + c - disp * (1.0 - pe) * grip;

          /* dispersion along the displacement, only inside the band */
          vec2 ca = disp * 0.045 * crest;
          vec3 c1, c2;
          c1.r = texture2D(uT1, cover(uvOld + ca, uRes, uS1)).r;
          c1.g = texture2D(uT1, cover(uvOld,      uRes, uS1)).g;
          c1.b = texture2D(uT1, cover(uvOld - ca, uRes, uS1)).b;
          c2.r = texture2D(uT2, cover(uvNew + ca, uRes, uS2)).r;
          c2.g = texture2D(uT2, cover(uvNew,      uRes, uS2)).g;
          c2.b = texture2D(uT2, cover(uvNew - ca, uRes, uS2)).b;

          vec3 col = mix(c1, c2, pe);

          /* crest glint, with a breath of #00E58A -- jewellery, not a laser */
          float glint = crest * crest * (0.5 + 0.5 * turb) * gate;
          col += glint * 0.16;
          col = mix(col, vec3(0.0, 0.898, 0.541), glint * 0.10);

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat));

    function render() { renderer.render(scene, cam); }

    /* Ken Burns: a slow push-in on whichever slide is resting. Linear ease
       (constant motion never "arrives"), calibrated to the autoplay dwell:
       by the time the slide changes it has pushed in ~7%, and a manually
       parked slide settles at 1.12. Killed (holding its current zoom, for
       seam continuity) when a transition starts; restarted from 1 after the
       swap. This whole module is skipped under prefers-reduced-motion. */
    const kb = { z: 1 };
    let kbTween = null;
    function kbStart() {
      if (kbTween) kbTween.kill();
      kb.z = 1;
      uniforms.uKb1.value = 1;
      render();
      if (!window.gsap) return;
      kbTween = window.gsap.to(kb, {
        z: 1.12, duration: Math.max(interval * 1.6, 8), ease: 'none',
        onUpdate() { uniforms.uKb1.value = kb.z; render(); },
      });
    }

    function size() {
      const w = root.clientWidth, h = root.clientHeight;
      if (!w || !h) return;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      renderer.setSize(w, h, false);
      uniforms.uRes.value.set(w, h);
      if (uniforms.uT1.value) render();
    }
    size();
    new ResizeObserver(size).observe(root);

    /* textures: reuse the browser's copy — currentSrc is what the <img>
       already downloaded, so the loader is a cache hit, not a re-fetch */
    const loader = new THREE.TextureLoader();
    const texes = new Array(n).fill(null);
    let dead = false;

    function load(i) {
      return new Promise((res, rej) => {
        const im = imgs[i];
        const url = (im.complete && im.currentSrc) || im.getAttribute('data-khs-tex');
        loader.load(url, (t) => {
          /* NoColorSpace on purpose: tagging these sRGB makes the GPU decode
             samples to linear, and a raw ShaderMaterial writes gl_FragColor
             with no output re-encode -- linear shown as sRGB = dark, shifted
             photos. Untagged, sRGB passes through untouched and the canvas
             matches the <img> stack exactly (the wave's mixing is happy in
             sRGB; nothing here does lighting math). */
          t.colorSpace = THREE.NoColorSpace;
          t.minFilter = THREE.LinearFilter;
          t.generateMipmaps = false;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
          texes[i] = t;
          res(t);
        }, undefined, rej);
      });
    }

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      dead = true;
      gl = null;
      if (kbTween) kbTween.kill();
      if (window.gsap) window.gsap.killTweensOf(canvas);
      canvas.hidden = true;
      medias.forEach((m, i) => m.classList.toggle('khs-off', i !== idx));
    });

    /* show slide i as a plain covered frame, zoom reset */
    function prime(i) {
      uniforms.uT1.value = texes[i];
      uniforms.uS1.value.set(texes[i].image.width, texes[i].image.height);
      uniforms.uP.value = 0;
      uniforms.uKb1.value = 1;
      render();
    }
    /* the wave can only start from a canvas the viewer is actually seeing */
    function shown() {
      return !canvas.hidden && +getComputedStyle(canvas).opacity > 0.99;
    }
    function loadRest() {
      /* a slide whose texture fails just keeps its crossfade fallback —
         texes[i] stays null and go()/swap() route around it */
      for (let i = 0; i < n; i++) if (imgs[i] && !texes[i]) load(i).catch(() => {});
    }

    function commit() {
      gl = {
        go(from, to, dir) {
          if (dead || !texes[to] || !texes[from] || !shown()) return false;
          /* park the idle push at its current zoom — uKb1 already holds it,
             so the outgoing frame carries straight on into the wave */
          if (kbTween) kbTween.kill();
          window.gsap.killTweensOf(canvas);
          uniforms.uKb2.value = 1;
          uniforms.uT1.value = texes[from];
          uniforms.uS1.value.set(texes[from].image.width, texes[from].image.height);
          uniforms.uT2.value = texes[to];
          uniforms.uS2.value.set(texes[to].image.width, texes[to].image.height);
          uniforms.uDir.value = dir;
          uniforms.uSeed.value = Math.random();   /* re-phase the wave — no two transitions alike */
          uniforms.uP.value = 0;
          window.gsap.to(uniforms.uP, {
            value: 1, duration: 1.35, ease: 'power2.inOut',
            onUpdate: render,
            onComplete() {
              uniforms.uT1.value = texes[to];
              uniforms.uS1.value.set(texes[to].image.width, texes[to].image.height);
              uniforms.uP.value = 0;
              kbStart();
            },
          });
          return true;
        },
        /* the crossfade leg: fade the canvas off the glow (or a photo whose
           texture is missing), and if the target HAS a photo, fade back in
           over it — the primed frame is pixel-identical to the <img> below,
           so the return fade is invisible — then push in again */
        swap(to) {
          if (dead || !window.gsap) return;
          if (kbTween) kbTween.kill();
          window.gsap.killTweensOf(canvas);
          window.gsap.to(canvas, {
            autoAlpha: 0, duration: 0.5, ease: 'power2.inOut',
            onComplete() {
              if (dead || !texes[to]) return;
              prime(to);
              window.gsap.to(canvas, { autoAlpha: 1, duration: 0.45, ease: 'power2.out', onComplete: kbStart });
            },
          });
        },
      };
    }

    if (imgs[idx]) {
      /* first frame before unveiling; remaining textures load behind it */
      load(idx).then(() => {
        if (dead) return;
        canvas.hidden = false;
        if (window.gsap) window.gsap.set(canvas, { autoAlpha: 1 });
        prime(idx);
        kbStart();
        loadRest();
        commit();
      }).catch(() => { /* texture failed — img stack stays in charge */ });
    } else {
      /* opening on a photo-less slide: the glow stack is the picture; the
         canvas idles transparent until swap() lands on a photo slide */
      canvas.hidden = false;
      if (window.gsap) window.gsap.set(canvas, { autoAlpha: 0 });
      loadRest();
      commit();
    }
  }
}
